import { type ReactNode } from 'react';

interface PopupOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: string;
  iconBg?: string;
  iconColor?: string;
  children: ReactNode;
  wide?: boolean;
}

export default function PopupOverlay({
  isOpen,
  onClose,
  title,
  subtitle,
  icon = 'ri-window-line',
  iconBg = 'bg-primary-500/10',
  iconColor = 'text-primary-400',
  children,
  wide = false,
}: PopupOverlayProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 md:pt-16 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Panel */}
      <div
        className={`relative w-full ${
          wide ? 'max-w-6xl' : 'max-w-4xl'
        } max-h-[85vh] bg-background-50 rounded-2xl border border-background-200/70 shadow-lg overflow-hidden flex flex-col animate-in`}
      >
        {/* Header */}
        <div className="shrink-0 px-5 py-4 border-b border-background-200/70 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
              <i className={`${icon} ${iconColor} text-sm`}></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-heading font-bold text-foreground-900 truncate">{title}</h2>
              {subtitle && (
                <p className="text-[11px] text-foreground-500 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-all cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </div>

      {/* Inline animation styles */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-in {
          animation: fadeSlideIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}